import { ReactNode, useState } from "react";

import Form from "@ant-design/compatible/es/form";
import {
    Button,
    InformationModal,
    ModalWidth,
    renderMarkdown,
} from "@impero-com/ui";
import { Input } from "antd";
import { cx } from "class-variance-authority";

import { t } from "../../utils/i18n";

import * as theme from "./DeleteWithConfirmationDialog.css";

const TEXT_TO_ENTER = "IMPERO";

export interface DeleteWithConfirmationDialogProps {
    title?: string;
    children?: ReactNode;
    width?: ModalWidth;
    open?: boolean;
    onConfirm: () => void;
    onCancel: () => void;
    /** Defaults to "Delete". */
    confirmLabel?: string;
    loading?: boolean;
    className?: string;
}

/** A destructive confirmation that stays locked until the user types `IMPERO`. */
export function DeleteWithConfirmationDialog({
    title,
    children,
    width,
    open = true,
    onConfirm,
    onCancel,
    confirmLabel,
    loading,
    className,
}: DeleteWithConfirmationDialogProps) {
    const [text, setText] = useState<string | undefined>(undefined);
    // Relock the dialog so a reopened one starts from scratch.
    if (!open && text !== undefined) {
        setText(undefined);
    }

    return (
        <InformationModal
            open={open}
            onClose={onCancel}
            type="error"
            icon="delete"
            title={title}
            width={width}
            className={cx(theme.dialog, className)}
            footer={
                <>
                    <Button onClick={onCancel}>{t("Cancel")}</Button>
                    <Button
                        variant="destructive"
                        disabled={text?.trim() !== TEXT_TO_ENTER}
                        loading={loading}
                        onClick={onConfirm}
                    >
                        {confirmLabel ?? t("Delete")}
                    </Button>
                </>
            }
        >
            {children}
            <Form.Item>
                {renderMarkdown(
                    t("Dialog_DeleteWithConfirmation_Text", {
                        text: TEXT_TO_ENTER,
                    })
                )}
                <Input
                    value={text}
                    onChange={(event) => setText(event.target.value)}
                />
            </Form.Item>
        </InformationModal>
    );
}
